import axios from 'axios';
import { SUBSCRIPTION_ENDPOINTS } from '../utils/constants';

class PaymentStatusChecker {
  constructor(paymentId, token, callbacks = {}) {
    this.paymentId = paymentId;
    this.token = token;
    this.callbacks = {
      onSuccess: callbacks.onSuccess || (() => {}),
      onExpired: callbacks.onExpired || (() => {}),
      onError: callbacks.onError || (() => {}),
      onStatusUpdate: callbacks.onStatusUpdate || (() => {})
    };
    
    this.isPolling = false;
    this.timeoutId = null;
    this.startTime = Date.now();
    this.maxDuration = 3 * 60 * 60 * 1000; // 3 hours in milliseconds
    this.intervalCount = 0;
    
    // Polling intervals: 30s, 30s, 1m, 1m, 2m, 2m, 5m, 5m, then 5m ongoing
    this.intervals = [30000, 30000, 60000, 60000, 120000, 120000, 300000];
    this.defaultInterval = 300000; // 5 minutes for ongoing polling
    
    this.storageKey = `payment_polling_${paymentId}`;
  }

  start() {
    if (this.isPolling) return;
    
    this.isPolling = true;
    this.savePollingState();
    
    console.log(`Starting payment status polling for payment ${this.paymentId}`);
    this.callbacks.onStatusUpdate('polling_started');
    
    // Start with immediate check
    this.checkStatus();
  }

  stop() {
    if (!this.isPolling) return;
    
    this.isPolling = false;
    
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    
    this.clearPollingState();
    console.log(`Stopped payment status polling for payment ${this.paymentId}`);
  }

  async checkStatus() {
    if (!this.isPolling) return;

    // Check if we've exceeded max duration
    if (Date.now() - this.startTime > this.maxDuration) {
      console.log('Polling timeout reached');
      this.callbacks.onError('Polling timeout reached. Please check your email or contact support.');
      this.stop();
      return;
    }

    try {
      const response = await axios.post(
        SUBSCRIPTION_ENDPOINTS.GET_CRYPTO_PAYMENT_STATUS,
        { paymentId: this.paymentId },
        {
          headers: {
            'Authorization': `Bearer ${this.token}`
          }
        }
      );

      if (response.data.success) {
        const { status, transactionHash, completedAt } = response.data.data;
        
        console.log(`Payment ${this.paymentId} status: ${status}`);
        this.callbacks.onStatusUpdate(status, { transactionHash, completedAt });

        switch (status) {
          case 'completed':
            console.log('Payment confirmed!');
            this.callbacks.onSuccess({ transactionHash, completedAt });
            this.stop();
            return;
          
          case 'expired':
            console.log('Payment expired');
            this.callbacks.onExpired();
            this.stop();
            return;
          
          case 'pending':
            // Continue polling
            this.scheduleNextCheck();
            break;
          
          default:
            console.log(`Unknown payment status: ${status}`);
            this.scheduleNextCheck();
        }
      } else {
        throw new Error(response.data.error || 'Failed to check payment status');
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      
      // Don't stop polling for network errors, just log and retry
      if (error.response?.status === 404) {
        this.callbacks.onError('Payment not found');
        this.stop();
        return;
      }
      
      // For other errors, continue polling but notify callback
      this.callbacks.onStatusUpdate('error', { error: error.message });
      this.scheduleNextCheck();
    }
  }

  scheduleNextCheck() {
    if (!this.isPolling) return;

    // Get current interval
    const currentInterval = this.intervalCount < this.intervals.length 
      ? this.intervals[this.intervalCount] 
      : this.defaultInterval;

    this.intervalCount++;

    console.log(`Scheduling next check in ${currentInterval / 1000} seconds`);
    
    this.timeoutId = setTimeout(() => {
      this.checkStatus();
    }, currentInterval);

    this.savePollingState();
  }

  savePollingState() {
    const state = {
      paymentId: this.paymentId,
      startTime: this.startTime,
      intervalCount: this.intervalCount,
      lastCheck: Date.now()
    };
    
    sessionStorage.setItem(this.storageKey, JSON.stringify(state));
  }

  clearPollingState() {
    sessionStorage.removeItem(this.storageKey);
  }

  // Static method to restore polling from saved state
  static restoreFromStorage(token, callbacks = {}) {
    const pollingStates = [];
    
    // Check all session storage keys for polling states
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith('payment_polling_')) {
        try {
          const state = JSON.parse(sessionStorage.getItem(key));
          
          // Check if state is not too old (24 hours)
          const hoursSinceStart = (Date.now() - state.startTime) / (1000 * 60 * 60);
          if (hoursSinceStart < 24) {
            pollingStates.push(state);
          } else {
            // Clean up old state
            sessionStorage.removeItem(key);
          }
        } catch (error) {
          console.error('Error parsing polling state:', error);
          sessionStorage.removeItem(key);
        }
      }
    }

    // Restore polling for found states
    const checkers = pollingStates.map(state => {
      const checker = new PaymentStatusChecker(state.paymentId, token, callbacks);
      checker.startTime = state.startTime;
      checker.intervalCount = state.intervalCount;
      checker.start();
      return checker;
    });

    return checkers;
  }

  // Static method to check if there are any pending payments
  static hasPendingPayments() {
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith('payment_polling_')) {
        return true;
      }
    }
    return false;
  }
}

export default PaymentStatusChecker;